"use client";

import { useMemo, useSyncExternalStore } from "react";
import { Link } from "@/i18n/navigation";
import { motion } from "framer-motion";
import { ToggleButton, ToggleButtonGroup } from "@heroui/react";
import { type BlogPost } from "@/lib/admin";
import { ArrowRight, Loader2 } from "@/components/icons";
import { Footer } from "@/components/footer";
import { Navbar } from "@/components/navbar";
import { COLLECTIONS } from "@/lib/academy";
import { useBlogPosts } from "@/features/blog/api/queries";

/* ──────────────────────────────────────────────────────────
   Blog - Linear-style index. Tag filters up top, card grid
   with gradient covers, an Academy showcase between rows,
   and a "Load more" pager.
   ────────────────────────────────────────────────────────── */

const TAGS = ["All", "Changelog", "Engineering", "Intelligence", "Risk", "AI", "Trust", "Company"];

/* The tag filter is stored in the `tags` query param rather than component
   state, so it survives a refresh. Reads go through useSyncExternalStore, which
   keeps the (empty) server snapshot separate from the client one. */
const tagListeners = new Set<() => void>();

function subscribeTags(cb: () => void) {
  tagListeners.add(cb);
  window.addEventListener("popstate", cb);
  return () => {
    tagListeners.delete(cb);
    window.removeEventListener("popstate", cb);
  };
}

function getTagsSnapshot() {
  return new URLSearchParams(window.location.search).get("tags") ?? "";
}

function getTagsServerSnapshot() {
  return "";
}

function writeTagsToUrl(next: Set<string>) {
  const url = new URL(window.location.href);
  if (next.size) url.searchParams.set("tags", [...next].join(","));
  else url.searchParams.delete("tags");
  window.history.replaceState(null, "", url);
  tagListeners.forEach((l) => l());
}

/* Gradient covers per tag - Linear-customers-style bold color blocks. */
const TAG_COVERS: Record<string, string[]> = {
  Changelog: [
    "bg-linear-to-br from-blue-600 via-blue-500 to-indigo-400",
    "bg-linear-to-b from-sky-500 to-blue-700",
  ],
  Engineering: [
    "bg-linear-to-br from-violet-600 via-purple-500 to-fuchsia-400",
    "bg-linear-to-b from-indigo-500 to-violet-700",
  ],
  Intelligence: [
    "bg-linear-to-br from-amber-400 via-orange-400 to-rose-400",
    "bg-linear-to-b from-orange-500 to-amber-600",
  ],
  Risk: [
    "bg-linear-to-br from-rose-500 via-red-500 to-orange-400",
    "bg-linear-to-b from-red-600 to-rose-500",
  ],
  AI: [
    "bg-linear-to-br from-emerald-500 via-teal-400 to-cyan-400",
    "bg-linear-to-b from-green-500 to-emerald-700",
  ],
  Trust: [
    "bg-linear-to-br from-cyan-500 via-sky-400 to-blue-400",
    "bg-linear-to-b from-teal-400 to-cyan-600",
  ],
  Company: [
    "bg-linear-to-br from-stone-500 via-stone-400 to-stone-300",
    "bg-linear-to-b from-zinc-500 to-stone-700",
  ],
};

function coverFor(post: BlogPost): string {
  const variants = TAG_COVERS[post.tag] ?? TAG_COVERS.Company;
  let h = 0;
  for (const c of post.slug) h = (h * 31 + c.charCodeAt(0)) | 0;
  return variants[Math.abs(h) % variants.length];
}

function BlogCard({ post }: { post: BlogPost }) {
  return (
    <Link href={`/blog/${post.slug}`} className="group flex flex-col">
      {/* Cover */}
      <div className={`relative aspect-8/5 overflow-hidden rounded-xl ${post.cover_image_url ? "" : coverFor(post)}`}>
        {post.cover_image_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={post.cover_image_url}
            alt=""
            className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <>
            {/* abstract cover art */}
            <div className="absolute -left-10 -top-12 size-44 rounded-full bg-white/15 blur-2xl transition-transform duration-500 group-hover:translate-x-2" />
            <div className="absolute -bottom-14 -right-8 size-48 rounded-full bg-black/20 blur-2xl" />
            <span className="absolute left-5 top-4 text-2xs font-medium uppercase tracking-widest text-white/70">
              {post.tag}
            </span>
            <span className="absolute bottom-4 left-5 right-5 line-clamp-2 text-lg font-semibold leading-snug tracking-tight text-white drop-shadow-sm">
              {post.title}
            </span>
          </>
        )}
      </div>

      {/* Meta */}
      <h3 className="mt-4 text-base font-medium leading-snug text-text-primary transition-colors group-hover:text-brand">
        {post.title}
      </h3>
      {post.excerpt && (
        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-text-tertiary">{post.excerpt}</p>
      )}
    </Link>
  );
}

/* ── Academy showcase - actual book covers, not a teaser box ── */

function AcademyShowcase() {
  const books = COLLECTIONS.slice(0, 5);
  return (
    <section className="my-20 overflow-hidden rounded-2xl border border-border-secondary bg-surface-raised/40">
      <div className="grid items-center gap-10 p-8 sm:p-12 lg:grid-cols-[auto_1fr]">
        <div className="max-w-sm">
          <p className="text-xs font-medium uppercase tracking-widest text-text-quaternary">The Academy</p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
            The curated library<br className="hidden sm:inline" /> of lookup intelligence.
          </h2>
          <Link
            href="/academy"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-brand transition-colors hover:text-brand-hover"
          >
            Browse the Academy
            <ArrowRight className="size-4" />
          </Link>
        </div>

        {/* Book shelf */}
        <div className="-mr-8 flex gap-10 overflow-x-auto py-2 pr-8 scrollbar-none mask-[linear-gradient(to_right,black_calc(100%-4rem),transparent)] [&::-webkit-scrollbar]:hidden lg:-mr-12">
          {books.map((c) => (
            <Link
              key={c.id}
              href={`/academy/${c.id}`}
              className="group relative block w-36 shrink-0 transition-transform duration-300 perspective-[1000px] hover:-translate-y-1.5 sm:w-40"
            >
              {/* page block - stacked paper edges */}
              <div className="absolute inset-y-2 -right-2 left-3 rounded-r-sm border border-stone-300 bg-stone-200 shadow-md" />
              <div className="absolute inset-y-1.5 -right-1.5 left-3 rounded-r-sm border border-stone-200 bg-stone-100" />
              <div className="absolute inset-y-1 -right-1 left-2 rounded-r-sm border border-stone-200 bg-white" />
              {/* first page - revealed when the cover opens */}
              <div className="absolute inset-y-0.5 left-2 right-0 overflow-hidden rounded-r-md border border-stone-200 bg-white">
                <div className="absolute inset-y-0 left-0 w-6 bg-linear-to-r from-stone-300/70 to-transparent" />
                <div className="absolute inset-x-4 top-5 space-y-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-1 rounded-full bg-stone-200" />
                  ))}
                </div>
              </div>

              {/* cover */}
              <div
                className={`relative flex aspect-3/4 origin-left flex-col justify-end overflow-hidden rounded-l-md rounded-r-lg p-3.5 shadow-xl transition-transform duration-500 ease-out transform-3d group-hover:transform-[rotateY(-32deg)] ${c.gradient}`}
              >
                {c.shapes.map((s, i) => (
                  <div key={i} className={s} />
                ))}
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-black/55 via-black/20 to-transparent" />
                <div className="absolute inset-y-0 left-0 w-5 bg-linear-to-r from-black/25 via-black/5 to-transparent" />
                <div className="absolute inset-y-0 left-1.5 w-px bg-white/40" />
                <div className="absolute inset-y-0 right-0 w-2 bg-linear-to-l from-black/15 to-transparent" />

                <span className="absolute left-4 top-3.5 text-3xs font-medium uppercase tracking-widest text-black/45">
                  The Academy
                </span>
                <c.icon className="absolute right-3.5 top-3.5 size-4 text-black/50" />

                <div className="relative pl-1.5">
                  <h3 className="text-sm font-semibold leading-snug text-white drop-shadow-sm">{c.title}</h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BlogPage() {
  // Empty set = "All". Single selection filters server-side; multiple
  // selections fetch everything and filter client-side (the API takes one tag).
  // The selection lives in the URL so a refresh, a shared link and Back/Forward
  // all keep it. The server snapshot is empty, so static markup still matches.
  const tagsParam = useSyncExternalStore(subscribeTags, getTagsSnapshot, getTagsServerSnapshot);
  const tags = useMemo(
    () => new Set(tagsParam.split(",").filter((t) => t !== "All" && TAGS.includes(t))),
    [tagsParam],
  );
  const setTags = writeTagsToUrl;

  const serverTag = tags.size === 1 ? [...tags][0] : "All";
  const {
    data,
    isLoading: loading,
    isFetchingNextPage: loadingMore,
    hasNextPage: hasMore,
    fetchNextPage,
  } = useBlogPosts(serverTag);
  const allPosts = data?.pages.flatMap((p) => p.posts) ?? [];
  const posts = tags.size > 1 ? allPosts.filter((p) => tags.has(p.tag)) : allPosts;

  const firstRows = posts.slice(0, 6);
  const rest = posts.slice(6);

  return (
    <div className="marketing-surfaces min-h-screen bg-surface-base text-text-primary flex flex-col">
      <Navbar />

      <main className="flex-1 px-5 sm:px-8 py-12">
        <div className="max-w-310 mx-auto w-full">
        {/* ── Hero ────────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 1, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="pt-12"
        >
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">Blog</h1>
        </motion.section>

        {/* Tag filters - sticky on mobile so the selection stays reachable while scrolling */}
        <div className="heroui sticky top-0 z-30 mt-7 mb-8 -mx-5 border-b border-border-secondary/60 bg-surface-base/90 px-5 py-3 backdrop-blur-md sm:static sm:mx-0 sm:mb-10 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-none">
          <ToggleButtonGroup
            aria-label="Filter posts by tag"
            selectionMode="multiple"
            isDetached
            size="sm"
            selectedKeys={tags.size === 0 ? ["All"] : tags}
            onSelectionChange={(keys) => {
              const next = new Set([...keys] as string[]);
              // "All" is only display-state for an empty filter. If it's in the
              // new keys while a real filter exists, the user clicked All: reset.
              // If it's there because the filter was empty, drop it and keep the
              // tag the user actually clicked.
              if (next.has("All") && tags.size > 0) setTags(new Set());
              else {
                next.delete("All");
                setTags(next);
              }
            }}
            className="flex-wrap justify-start! gap-2"
          >
            {TAGS.map((t) => (
              <ToggleButton key={t} id={t}>
                {t}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>

        {/* ── Posts ───────────────────────────────────── */}
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="size-6 animate-spin text-text-quaternary" />
          </div>
        ) : posts.length === 0 ? (
          <div className="py-32 text-center">
            <p className="text-base text-text-tertiary">No posts yet{tags.size > 0 ? ` in ${[...tags].join(", ")}` : ""}.</p>
          </div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 1, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 gap-x-12 gap-y-10 sm:grid-cols-2 sm:gap-y-14 lg:grid-cols-3 lg:gap-y-20"
            >
              {firstRows.map((p) => (
                <BlogCard key={p.id} post={p} />
              ))}
            </motion.div>

            {/* Academy showcase hidden while the Academy is unlisted. */}
            {/* <AcademyShowcase /> */}

            {rest.length > 0 && (
              <div className="grid grid-cols-1 gap-x-12 gap-y-10 sm:grid-cols-2 sm:gap-y-14 lg:grid-cols-3 lg:gap-y-20">
                {rest.map((p) => (
                  <BlogCard key={p.id} post={p} />
                ))}
              </div>
            )}

            {/* Load more */}
            {hasMore && (
              <div className="mt-16 flex justify-center">
                <button
                  type="button"
                  disabled={loadingMore}
                  onClick={() => fetchNextPage()}
                  className="inline-flex items-center gap-2 rounded-xl border border-border-secondary px-6 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-raised hover:text-text-primary disabled:opacity-50 cursor-pointer"
                >
                  {loadingMore && <Loader2 className="size-4 animate-spin" />}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
